/**
 * leaderReviewService.ts
 * Unified service for the Leader EPR Review module.
 * Replaces: leaderEPRService (peer OPTICS flow) + roundTableService (full RT flow)
 *
 * Firestore collections:
 *   leaderReviewSessions        — session header with modular score weights
 *   leaderReviewEnrollments     — enrolled leaders per session
 *   leaderReviewSelfAssessments — leader self-assessments (8 Muscles)
 *   leaderReviewPeerRatings     — peer ratings (each leader rates every other)
 *   leaderReviewLiveNotes       — admin live-session notes with optional score
 *   leaderReviewResults         — consolidated results (visible to all leaders)
 */

import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  getDocs, getDoc, query, where, orderBy,
  serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { fetchKRsForReview } from './evidenceService';
import { LEADER_MUSCLES } from '../types';
import type {
  LeaderReviewSession, LeaderReviewStatus, LeaderReviewTemplate,
  LeaderReviewEnrollment, LeaderReviewSelfAssessment, LeaderMuscleScore,
  LeaderReviewPeerRating, LeaderReviewLiveNote, LeaderReviewResult,
  KRProgressEntry, LeaderMuscle, KRReflection,
} from '../types';

// ─── Collection names ─────────────────────────────────────────────────────────

const COL_SESSIONS   = 'leaderReviewSessions';
const COL_ENROLLS    = 'leaderReviewEnrollments';
const COL_SELF       = 'leaderReviewSelfAssessments';
const COL_PEERS      = 'leaderReviewPeerRatings';
const COL_LIVE       = 'leaderReviewLiveNotes';
const COL_RESULTS    = 'leaderReviewResults';
const COL_TEMPLATES  = 'leaderReviewTemplates';

// ─── Helper ───────────────────────────────────────────────────────────────────

function snap<T>(d: { id: string; data: () => Record<string, unknown> }): T {
  return { id: d.id, ...d.data() } as T;
}

// ─── KR delivery insight (computed, no AI) ────────────────────────────────────

export function computeKRInsight(krs: KRProgressEntry[]): string {
  if (!krs.length) return 'No KRs synced yet — check your OKR Tracker assignment.';
  const achieved = krs.filter(k => (k.progress ?? 0) >= 100 || (k.krStatus ?? '').toLowerCase().includes('achiev')).length;
  const pct = Math.round((achieved / krs.length) * 100);
  if (pct >= 80) return 'Strong delivery this period. Your KRs set a solid foundation — use reflection to show the depth behind the numbers.';
  if (pct >= 50) return 'Solid progress with some KRs still in motion. Be specific about what you are actively driving.';
  if (pct > 0)   return 'Several KRs are still in progress. This reflection is a chance to give context to the work ahead.';
  return 'Work in progress — your reflection here is especially important context for the group.';
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATES
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchTemplates(): Promise<LeaderReviewTemplate[]> {
  const s = await getDocs(query(collection(db, COL_TEMPLATES), orderBy('createdAt', 'desc')));
  return s.docs.map(d => snap<LeaderReviewTemplate>(d));
}

export async function fetchTemplate(templateId: string): Promise<LeaderReviewTemplate | null> {
  const d = await getDoc(doc(db, COL_TEMPLATES, templateId));
  return d.exists() ? snap<LeaderReviewTemplate>(d) : null;
}

export async function createTemplate(
  data: { name: string; description?: string; muscles: LeaderMuscle[]; isDefault?: boolean; createdBy: string },
): Promise<string> {
  const ref = await addDoc(collection(db, COL_TEMPLATES), {
    ...data,
    isDefault: data.isDefault ?? false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateTemplate(
  templateId: string,
  data: Partial<Pick<LeaderReviewTemplate, 'name' | 'description' | 'muscles' | 'isDefault'>>,
): Promise<void> {
  await updateDoc(doc(db, COL_TEMPLATES, templateId), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteTemplate(templateId: string): Promise<void> {
  await deleteDoc(doc(db, COL_TEMPLATES, templateId));
}

/** Resolve muscles for a session: custom template or default LEADER_MUSCLES */
export async function resolveSessionMuscles(session: LeaderReviewSession): Promise<LeaderMuscle[]> {
  if (!session.templateId) return LEADER_MUSCLES;
  const tmpl = await fetchTemplate(session.templateId);
  return tmpl?.muscles ?? LEADER_MUSCLES;
}

// ═══════════════════════════════════════════════════════════════════════════════
// SESSIONS
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchSessions(): Promise<LeaderReviewSession[]> {
  const s = await getDocs(query(collection(db, COL_SESSIONS), orderBy('createdAt', 'desc')));
  return s.docs.map(d => snap<LeaderReviewSession>(d));
}

export async function fetchSession(sessionId: string): Promise<LeaderReviewSession | null> {
  const d = await getDoc(doc(db, COL_SESSIONS, sessionId));
  return d.exists() ? snap<LeaderReviewSession>(d) : null;
}

export async function fetchActiveSession(): Promise<LeaderReviewSession | null> {
  // Include 'setup' so enrolled leaders can see the upcoming session
  const s = await getDocs(
    query(collection(db, COL_SESSIONS), where('status', 'in', ['setup', 'self_review', 'peer_review', 'live', 'consolidation'])),
  );
  if (s.empty) return null;
  // Prefer non-setup sessions (actually in progress) over setup ones
  const sorted = s.docs.map(d => snap<LeaderReviewSession>(d)).sort((a, b) => {
    const order = ['self_review', 'peer_review', 'live', 'consolidation', 'setup'];
    return order.indexOf(a.status) - order.indexOf(b.status);
  });
  return sorted[0];
}

export async function createSession(
  data: Omit<LeaderReviewSession, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const ref = await addDoc(collection(db, COL_SESSIONS), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateSession(
  sessionId: string,
  data: Partial<Omit<LeaderReviewSession, 'id' | 'createdAt'>>,
): Promise<void> {
  await updateDoc(doc(db, COL_SESSIONS, sessionId), { ...data, updatedAt: serverTimestamp() });
}

export async function advanceSessionStatus(
  sessionId: string,
  newStatus: LeaderReviewStatus,
): Promise<void> {
  await updateDoc(doc(db, COL_SESSIONS, sessionId), {
    status: newStatus,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteSession(sessionId: string): Promise<void> {
  await deleteDoc(doc(db, COL_SESSIONS, sessionId));
}

/**
 * Checks if a session should auto-advance based on:
 *   setup       → self_review : selfReviewStartDate has arrived AND leaders are enrolled
 *   self_review → peer_review : all leaders submitted self-review  OR  selfReviewDeadline passed
 *   peer_review → live        : all leaders done with peers        OR  peerReviewDeadline passed
 *
 * Returns the new status if it advanced, null if nothing changed.
 * Safe to call on every page load — idempotent.
 */
export async function checkAndAutoAdvance(
  session: LeaderReviewSession,
  enrollments: LeaderReviewEnrollment[],
): Promise<LeaderReviewStatus | null> {
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  let nextStatus: LeaderReviewStatus | null = null;
  let reason = '';

  if (session.status === 'setup') {
    // Only auto-open if a start date is set, it has arrived, and at least one leader is enrolled
    if (
      session.selfReviewStartDate &&
      session.selfReviewStartDate <= today &&
      enrollments.length > 0
    ) {
      nextStatus = 'self_review';
      reason = `selfReviewStartDate (${session.selfReviewStartDate}) reached`;
    }
  }

  else if (session.status === 'self_review') {
    const allSubmitted = enrollments.length > 0 && enrollments.every(e => e.selfSubmitted);
    const deadlinePassed = !!session.selfReviewDeadline && session.selfReviewDeadline < today;
    if (allSubmitted) {
      nextStatus = 'peer_review';
      reason = 'all leaders submitted self-review';
    } else if (deadlinePassed) {
      nextStatus = 'peer_review';
      reason = `selfReviewDeadline (${session.selfReviewDeadline}) passed`;
    }
  }

  else if (session.status === 'peer_review') {
    const allDone = enrollments.length > 0 && enrollments.every(
      e => e.totalPeerCount === 0 || e.peerSubmittedCount >= e.totalPeerCount,
    );
    const deadlinePassed = !!session.peerReviewDeadline && session.peerReviewDeadline < today;
    if (allDone) {
      nextStatus = 'live';
      reason = 'all leaders completed peer reviews';
    } else if (deadlinePassed) {
      nextStatus = 'live';
      reason = `peerReviewDeadline (${session.peerReviewDeadline}) passed`;
    }
  }

  if (nextStatus) {
    console.info(`[LeaderEPR] Auto-advancing "${session.name}" → ${nextStatus} (${reason})`);
    await advanceSessionStatus(session.id, nextStatus);
    return nextStatus;
  }

  return null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// LEADER USERS (from OKR Tracker users collection)
// ═══════════════════════════════════════════════════════════════════════════════

export interface LeaderUser {
  uid: string;
  name: string;
  email: string;
  jobTitle?: string;
}

export async function fetchLeaderUsers(): Promise<LeaderUser[]> {
  const s = await getDocs(
    query(collection(db, 'users'), where('isLeader', '==', true), where('isActive', '==', true)),
  );
  return s.docs.map(d => {
    const data = d.data();
    return {
      uid: d.id,
      name: data.name ?? data.displayName ?? d.id,
      email: data.email ?? '',
      jobTitle: data.jobTitle ?? '',
    } as LeaderUser;
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// ENROLLMENTS
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchEnrollments(sessionId: string): Promise<LeaderReviewEnrollment[]> {
  const s = await getDocs(
    query(collection(db, COL_ENROLLS), where('sessionId', '==', sessionId), orderBy('leaderName')),
  );
  return s.docs.map(d => snap<LeaderReviewEnrollment>(d));
}

export async function fetchMyEnrollment(
  sessionId: string,
  leaderId: string,
): Promise<LeaderReviewEnrollment | null> {
  const s = await getDocs(
    query(collection(db, COL_ENROLLS), where('sessionId', '==', sessionId), where('leaderId', '==', leaderId)),
  );
  if (s.empty) return null;
  return snap<LeaderReviewEnrollment>(s.docs[0]);
}

/** Strip undefined values from an object so Firestore doesn't throw on undefined fields */
function stripUndefined<T extends object>(obj: T): T {
  return JSON.parse(JSON.stringify(obj)) as T;
}

export async function enrollLeader(
  sessionId: string,
  leader: { leaderId: string; leaderName: string; leaderEmail: string; leaderJobTitle?: string },
): Promise<string> {
  // Pull KR data for context
  let krSummary: KRProgressEntry[] = [];
  let krDeliveryPct = 0;
  try {
    const raw = await fetchKRsForReview(leader.leaderId);
    // Strip undefined optional fields — Firestore rejects undefined values
    krSummary = raw.map(k => stripUndefined(k));
    const achieved = krSummary.filter(k => (k.progress ?? 0) >= 100 || (k.krStatus ?? '').toLowerCase().includes('achiev')).length;
    krDeliveryPct = krSummary.length ? Math.round((achieved / krSummary.length) * 100) : 0;
  } catch {
    // KR fetch optional — proceed without
  }

  const ref = await addDoc(collection(db, COL_ENROLLS), {
    sessionId,
    leaderId: leader.leaderId,
    leaderName: leader.leaderName,
    leaderEmail: leader.leaderEmail ?? '',
    leaderJobTitle: leader.leaderJobTitle ?? '',
    krSummary,
    krDeliveryPct,
    selfSubmitted: false,
    peerSubmittedCount: 0,
    totalPeerCount: 0,
    finalScore: null,
    published: false,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function removeEnrollment(enrollmentId: string): Promise<void> {
  await deleteDoc(doc(db, COL_ENROLLS, enrollmentId));
}

export async function refreshEnrollmentKRs(
  enrollmentId: string,
  leaderId: string,
): Promise<void> {
  try {
    const raw = await fetchKRsForReview(leaderId);
    const krs = raw.map(k => stripUndefined(k));
    const achieved = krs.filter(k => (k.progress ?? 0) >= 100 || (k.krStatus ?? '').toLowerCase().includes('achiev')).length;
    const pct = krs.length ? Math.round((achieved / krs.length) * 100) : 0;
    await updateDoc(doc(db, COL_ENROLLS, enrollmentId), {
      krSummary: krs,
      krDeliveryPct: pct,
    });
  } catch {
    // silently ignore
  }
}

/** After enrolling all leaders, set each leader's totalPeerCount = (total leaders - 1) */
export async function syncPeerCounts(sessionId: string): Promise<void> {
  const enrollments = await fetchEnrollments(sessionId);
  const total = enrollments.length;
  const batch = writeBatch(db);
  for (const e of enrollments) {
    batch.update(doc(db, COL_ENROLLS, e.id), { totalPeerCount: total - 1 });
  }
  await batch.commit();
}

// ═══════════════════════════════════════════════════════════════════════════════
// SELF ASSESSMENTS
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchSelfAssessment(
  sessionId: string,
  leaderId: string,
): Promise<LeaderReviewSelfAssessment | null> {
  const s = await getDocs(
    query(collection(db, COL_SELF), where('sessionId', '==', sessionId), where('leaderId', '==', leaderId)),
  );
  if (s.empty) return null;
  return snap<LeaderReviewSelfAssessment>(s.docs[0]);
}

export async function saveSelfAssessmentDraft(
  sessionId: string,
  leaderId: string,
  scores: LeaderMuscleScore[],
  krReflections?: KRReflection[],
): Promise<void> {
  const existing = await fetchSelfAssessment(sessionId, leaderId);
  if (existing) {
    await updateDoc(doc(db, COL_SELF, existing.id), {
      scores,
      ...(krReflections !== undefined ? { krReflections } : {}),
      updatedAt: serverTimestamp(),
    });
  } else {
    await addDoc(collection(db, COL_SELF), {
      sessionId, leaderId, scores,
      ...(krReflections !== undefined ? { krReflections } : {}),
      submittedAt: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
}

export async function submitSelfAssessment(
  sessionId: string,
  leaderId: string,
  scores: LeaderMuscleScore[],
  krReflections?: KRReflection[],
): Promise<void> {
  const existing = await fetchSelfAssessment(sessionId, leaderId);
  if (existing) {
    await updateDoc(doc(db, COL_SELF, existing.id), {
      scores,
      ...(krReflections !== undefined ? { krReflections } : {}),
      submittedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } else {
    await addDoc(collection(db, COL_SELF), {
      sessionId, leaderId, scores,
      ...(krReflections !== undefined ? { krReflections } : {}),
      submittedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
  // Mark enrollment as self-submitted
  const s = await getDocs(
    query(collection(db, COL_ENROLLS), where('sessionId', '==', sessionId), where('leaderId', '==', leaderId)),
  );
  if (!s.empty) {
    await updateDoc(doc(db, COL_ENROLLS, s.docs[0].id), { selfSubmitted: true });
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// PEER RATINGS
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchMyPeerRatings(
  sessionId: string,
  raterId: string,
): Promise<LeaderReviewPeerRating[]> {
  const s = await getDocs(
    query(collection(db, COL_PEERS), where('sessionId', '==', sessionId), where('raterId', '==', raterId)),
  );
  return s.docs.map(d => snap<LeaderReviewPeerRating>(d));
}

export async function fetchRatingsForLeader(
  sessionId: string,
  ratedLeaderId: string,
): Promise<LeaderReviewPeerRating[]> {
  const s = await getDocs(
    query(collection(db, COL_PEERS), where('sessionId', '==', sessionId), where('ratedLeaderId', '==', ratedLeaderId)),
  );
  return s.docs.map(d => snap<LeaderReviewPeerRating>(d));
}

export async function submitPeerRating(
  sessionId: string,
  raterId: string,
  ratedLeaderId: string,
  scores: LeaderMuscleScore[],
  strength?: string,
  growthArea?: string,
): Promise<void> {
  // Check if already rated
  const existing = await getDocs(
    query(collection(db, COL_PEERS),
      where('sessionId', '==', sessionId),
      where('raterId', '==', raterId),
      where('ratedLeaderId', '==', ratedLeaderId),
    ),
  );
  if (!existing.empty) {
    await updateDoc(doc(db, COL_PEERS, existing.docs[0].id), {
      scores, strength: strength ?? '', growthArea: growthArea ?? '',
      submittedAt: serverTimestamp(),
    });
  } else {
    await addDoc(collection(db, COL_PEERS), {
      sessionId, raterId, ratedLeaderId, scores,
      strength: strength ?? '',
      growthArea: growthArea ?? '',
      submittedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    });
  }
  // Update rater's peer submitted count
  const raterEnroll = await getDocs(
    query(collection(db, COL_ENROLLS), where('sessionId', '==', sessionId), where('leaderId', '==', raterId)),
  );
  if (!raterEnroll.empty) {
    const e = raterEnroll.docs[0].data() as LeaderReviewEnrollment;
    const allRated = await fetchMyPeerRatings(sessionId, raterId);
    await updateDoc(doc(db, COL_ENROLLS, raterEnroll.docs[0].id), {
      peerSubmittedCount: allRated.length,
      totalPeerCount: Math.max(e.totalPeerCount, allRated.length),
    });
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// LIVE NOTES
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchLiveNotes(
  sessionId: string,
  leaderId: string,
): Promise<LeaderReviewLiveNote[]> {
  const s = await getDocs(
    query(collection(db, COL_LIVE), where('sessionId', '==', sessionId), where('leaderId', '==', leaderId), orderBy('createdAt')),
  );
  return s.docs.map(d => snap<LeaderReviewLiveNote>(d));
}

export async function addLiveNote(data: Omit<LeaderReviewLiveNote, 'id' | 'createdAt'>): Promise<string> {
  const ref = await addDoc(collection(db, COL_LIVE), { ...data, createdAt: serverTimestamp() });
  return ref.id;
}

export async function updateLiveNote(noteId: string, content: string, liveScore?: number): Promise<void> {
  await updateDoc(doc(db, COL_LIVE, noteId), { content, ...(liveScore !== undefined ? { liveScore } : {}) });
}

// ═══════════════════════════════════════════════════════════════════════════════
// RESULTS & CONSOLIDATION
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchResult(
  sessionId: string,
  leaderId: string,
): Promise<LeaderReviewResult | null> {
  const s = await getDocs(
    query(collection(db, COL_RESULTS), where('sessionId', '==', sessionId), where('leaderId', '==', leaderId)),
  );
  if (s.empty) return null;
  return snap<LeaderReviewResult>(s.docs[0]);
}

export async function fetchAllResults(sessionId: string): Promise<LeaderReviewResult[]> {
  const s = await getDocs(
    query(collection(db, COL_RESULTS), where('sessionId', '==', sessionId), orderBy('leaderName')),
  );
  return s.docs.map(d => snap<LeaderReviewResult>(d));
}

export async function saveConsolidatedResult(data: Omit<LeaderReviewResult, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> {
  const existing = await fetchResult(data.sessionId, data.leaderId);
  if (existing) {
    await updateDoc(doc(db, COL_RESULTS, existing.id), { ...data, updatedAt: serverTimestamp() });
  } else {
    await addDoc(collection(db, COL_RESULTS), { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  }
}

/** Publish results — marks session completed and marks all enrollments as published */
export async function publishResults(sessionId: string): Promise<void> {
  await updateDoc(doc(db, COL_SESSIONS, sessionId), { status: 'completed', updatedAt: serverTimestamp() });
  const enrollments = await fetchEnrollments(sessionId);
  const batch = writeBatch(db);
  for (const e of enrollments) {
    batch.update(doc(db, COL_ENROLLS, e.id), { published: true });
  }
  await batch.commit();
}

// ═══════════════════════════════════════════════════════════════════════════════
// SCORE ENGINE
// ═══════════════════════════════════════════════════════════════════════════════

/** Average per-muscle scores across multiple raters */
export function aggregatePeerScores(ratings: LeaderReviewPeerRating[]): LeaderMuscleScore[] {
  if (!ratings.length) return [];
  const totals: Record<string, { sum: number; count: number }> = {};
  for (const rating of ratings) {
    for (const s of rating.scores) {
      if (!totals[s.muscleKey]) totals[s.muscleKey] = { sum: 0, count: 0 };
      totals[s.muscleKey].sum += s.score;
      totals[s.muscleKey].count += 1;
    }
  }
  return LEADER_MUSCLES.map(m => ({
    muscleKey: m.key,
    score: totals[m.key] ? Math.round((totals[m.key].sum / totals[m.key].count) * 10) / 10 : 0,
  }));
}

/** Weighted blend using session score weights */
export function computeConsolidatedScore(
  peerAvgScores: LeaderMuscleScore[],
  selfScores: LeaderMuscleScore[],
  weights: { peer: number; self: number; live: number },
  liveScore?: number,
): number {
  const peerAvg = peerAvgScores.reduce((s, m) => s + m.score, 0) / (peerAvgScores.length || 1);
  const selfAvg = selfScores.reduce((s, m) => s + m.score, 0) / (selfScores.length || 1);

  if (liveScore !== undefined) {
    return Math.round((peerAvg * weights.peer + selfAvg * weights.self + liveScore * weights.live) * 10) / 10;
  }
  // No live score — redistribute live weight proportionally
  const total = weights.peer + weights.self;
  return Math.round((peerAvg * (weights.peer / total) + selfAvg * (weights.self / total)) * 10) / 10;
}
